import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Button, Modal, Segmented } from '@w7/ui'

export type PortalTheme = 'dark' | 'light'

export interface ThemePrefs {
  theme: PortalTheme
}

const THEME_KEY = 'w7.prefs.theme'
export const DEFAULT_THEME_PREFS: ThemePrefs = { theme: 'dark' }

export function readThemePrefs(): ThemePrefs {
  try {
    const theme = window.localStorage.getItem(THEME_KEY)
    return { theme: theme === 'light' ? 'light' : 'dark' }
  } catch {
    return { ...DEFAULT_THEME_PREFS }
  }
}

export function writeThemePrefs(prefs: ThemePrefs) {
  try {
    window.localStorage.setItem(THEME_KEY, prefs.theme)
  } catch {
    /* private mode */
  }
}

export function applyPrefsToDocument(prefs: ThemePrefs, opts?: { animate?: boolean }) {
  const root = document.documentElement
  if (opts?.animate) {
    root.classList.add('w7-theme-animating')
    window.setTimeout(() => root.classList.remove('w7-theme-animating'), 320)
  }
  root.setAttribute('data-theme', prefs.theme)
  root.style.colorScheme = prefs.theme
}

interface ThemeContextValue extends ThemePrefs {
  setTheme: (theme: PortalTheme) => void
  openPreferences: () => void
  closePreferences: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function useThemePrefs(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useThemePrefs must be used inside ThemeProvider')
  return ctx
}

function PreferencesModal({
  open,
  onClose,
  theme,
  setTheme,
}: {
  open: boolean
  onClose: () => void
  theme: PortalTheme
  setTheme: (theme: PortalTheme) => void
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Appearance"
      subtitle="Portal theme. Saved on this device."
      footer={
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="w7-col" style={{ gap: 'var(--w7-s3)' }}>
        <div>
          <p className="w7-label" style={{ marginBottom: 4 }}>
            Portal theme
          </p>
          <p className="w7-card-sub">Applies across every page in the console.</p>
        </div>
        <Segmented
          label="Portal theme"
          value={theme}
          onChange={setTheme}
          items={[
            { id: 'dark', label: 'Dark' },
            { id: 'light', label: 'Light' },
          ]}
        />
      </div>
    </Modal>
  )
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<ThemePrefs>(() =>
    typeof window === 'undefined' ? DEFAULT_THEME_PREFS : readThemePrefs(),
  )
  const [open, setOpen] = useState(false)
  const firstApply = useRef(true)

  useLayoutEffect(() => {
    applyPrefsToDocument(prefs, { animate: !firstApply.current })
    firstApply.current = false
    writeThemePrefs(prefs)
  }, [prefs])

  const setTheme = useCallback((theme: PortalTheme) => {
    setPrefs({ theme })
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({
      ...prefs,
      setTheme,
      openPreferences: () => setOpen(true),
      closePreferences: () => setOpen(false),
    }),
    [prefs, setTheme],
  )

  return (
    <ThemeContext.Provider value={value}>
      {children}
      <PreferencesModal
        open={open}
        onClose={() => setOpen(false)}
        theme={prefs.theme}
        setTheme={setTheme}
      />
    </ThemeContext.Provider>
  )
}
